import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Text,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { ConversationMessage, ReportReason, ReplyToInfo } from '@/types/residents/marketplaceListing';
import { useListingNavigation } from '@/hooks/residents/useListingNavigation';
import { useToast } from '@/contexts/admin/ToastContext';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { showConfirmWithButtons } from '@/utils/common/alert';
import { MessageActionsModal } from '@/components/residents/marketplace/MessageActionsModal';
import { ReportModal } from '@/components/residents/marketplace/ReportModal';
import AttachmentPreview from '@/components/residents/propertyChat/mobile/AttachmentPreview';
import {
  DateSeparator,
  SentMessageBubble,
  ReceivedMessageBubble,
  ChatInputBar,
  ChatHeader,
  ReplyPreview,
  groupMessagesByDate,
  chatStyles,
} from '@/components/residents/marketplace/shared';
import { MARKETPLACE_MAX_CONTENT_WIDTH } from '@/utils/marketplace';
import { 
  useGetConversationQuery, 
  useSendMessageMutation,
  useLazyGetListingQuery,
  useCreateReportMutation,
} from '@/store/api';

type InboxConversationNavigationProp = StackNavigationProp<RootStackParamList, 'InboxConversation'>;
type InboxConversationRouteProp = RouteProp<RootStackParamList, 'InboxConversation'>;

export const InboxConversationScreen: React.FC = () => {
  const navigation = useNavigation<InboxConversationNavigationProp>();
  const route = useRoute<InboxConversationRouteProp>();
  const insets = useSafeAreaInsets();
  const scrollViewRef = useRef<ScrollView>(null);
  const textInputRef = useRef<TextInput>(null);
  const { showSuccess, showError } = useToast();
  const { layoutMode } = useResponsive();
  const { navigateToListing } = useListingNavigation();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Max chat width for larger screens
  const maxChatWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Get conversationId from route params
  const { conversationId } = route.params;
  
  // Pagination state for messages
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Fetch conversation with messages from API with polling
  const { 
    data: conversationData, 
    isLoading, 
    isError,
    refetch,
    isFetching,
  } = useGetConversationQuery({ conversationId, pageNum: currentPage }, {
    pollingInterval: 10000, // Poll every 10 seconds for new messages
  });
  
  // Pagination helpers
  const hasMoreMessages = conversationData?.pagination?.hasMore ?? false;
  
  const loadMoreMessages = () => {
    if (isLoadingMore || isFetching || !hasMoreMessages) return;
    setIsLoadingMore(true);
    setCurrentPage((prev) => prev + 1);
    setIsLoadingMore(false);
  };
  
  // Send message mutation
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  
  // Lazy query to fetch full listing when header is pressed
  const [getListing] = useLazyGetListingQuery();
  
  // Create report mutation
  const [createReport, { isLoading: isReporting }] = useCreateReportMutation();
  
  const [messageText, setMessageText] = useState('');
  
  // Attachment handling (reusing property chat hook)
  const { 
    attachments,
    filesForApi, // Already in API-ready format
    addFromCamera, 
    addFromLibrary, 
    addFromWebFilePicker,
    removeAttachment, 
    resetAttachments,
  } = useChatAttachments();
  
  // Modal visibility states
  const [isActionsModalVisible, setIsActionsModalVisible] = useState(false);
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [hasReportedConversation, setHasReportedConversation] = useState(false);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<ReplyToInfo | null>(null);
  const [selectedMessageForAction, setSelectedMessageForAction] = useState<ConversationMessage | null>(null);

  // Hide navigation header
  useEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // Auto scroll to bottom when messages change
  useEffect(() => {
    if (conversationData?.messages && conversationData.messages.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [conversationData?.messages]);

  const handleClose = () => {
    navigation.goBack();
  };

  // Navigate to item detail when header is pressed
  const handleHeaderPress = async () => {
    const listingId = conversationData?.listing?.id;
    if (!listingId) return;
    
    try {
      const result = await getListing(listingId).unwrap();
      if (result) {
        navigateToListing(result);
      }
    } catch (error) {
      console.error('Failed to fetch listing:', error);
      showError('Unable to load item details');
    }
  };

  const handleSend = async () => {
    const trimmedMessage = messageText.trim();
    const hasFiles = attachments.length > 0;
    
    // Require either text or files
    if ((!trimmedMessage && !hasFiles) || isSending) return;
    
    // Clear input immediately for better UX
    setMessageText('');
    const filesToSend = filesForApi; // Already in API-ready format
    resetAttachments();
    const replyToIdToSend = replyingTo?.id;
    setReplyingTo(null); // Clear reply state immediately
    
    try {
      await sendMessage({
        conversationId,
        text: trimmedMessage || undefined,
        files: filesToSend,
        replyToId: replyToIdToSend,
      }).unwrap();
      
      // Refetch to get the new message with server timestamp
      refetch();
    } catch (error) {
      console.error('Failed to send message:', error);
      showError('Failed to send message. Please try again.');
      // Restore the message text on error (attachments are already gone)
      setMessageText(trimmedMessage);
    }
  };

  const handleAttach = () => {
    // On web, use the web file picker with image-only MIME type validation
    if (Platform.OS === 'web') {
      addFromWebFilePicker();
      return;
    }
    // On native, show camera/library options
    showConfirmWithButtons(
      'Add Photos',
      'Choose how you want to add photos',
      'Photo Library',
      'Take Photo',
      addFromLibrary,
      addFromCamera
    );
  };

  // Handle opening actions menu
  const handleOpenActionsMenu = (message?: ConversationMessage) => {
    if (message) {
      setSelectedMessageForAction(message);
    }
    setIsActionsModalVisible(true);
  };

  // Handle reply action from actions modal
  const handleReply = () => {
    setIsActionsModalVisible(false);
    
    // Set the message being replied to
    if (selectedMessageForAction) {
      const senderName = selectedMessageForAction.sender === 'user' 
        ? 'You' 
        : (conversationData?.otherUser?.name || 'User');
      
      setReplyingTo({
        id: selectedMessageForAction.id,
        text: selectedMessageForAction.text,
        senderName,
      });
      setSelectedMessageForAction(null);
    }
    
    // Focus the text input for reply
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 100);
  };

  // Clear reply state
  const clearReply = () => {
    setReplyingTo(null);
  };

  // Handle opening report modal from actions modal
  const handleOpenReportModal = () => {
    setIsActionsModalVisible(false);
    setIsReportModalVisible(true);
  };

  // Handle report submission
  const handleSubmitReport = async (reason: ReportReason, description?: string) => {
    if (isReporting) return;
    
    try {
      await createReport({
        conversationId,
        reportedUserId: conversationData?.otherUser?.id,
        reason,
        description,
      }).unwrap();
      
      // Close modal and mark as reported
      setIsReportModalVisible(false);
      setHasReportedConversation(true);
      
      // Show success toast
      showSuccess('Report submitted. It will be reviewed by the moderation team.');
    } catch (error: any) {
      console.error('Failed to submit report:', error);
      
      // Check if already reported (conflict error)
      if (error?.status === 409) {
        setIsReportModalVisible(false);
        setHasReportedConversation(true);
      } else {
        showError('Failed to submit report. Please try again.');
      }
    }
  };

  // Render grouped messages
  const renderMessages = () => {
    const messages = conversationData?.messages || [];
    if (messages.length === 0) {
      return (
        <View style={styles.emptyMessages}>
          <Text style={styles.emptyMessagesText}>
            Send a message to start the conversation
          </Text>
        </View>
      );
    }

    const groupedMessages = groupMessagesByDate(messages);

    return (
      <>
        {/* Load More Messages Button */}
        {hasMoreMessages && (
          <View style={styles.loadMoreContainer}>
            {isLoadingMore || isFetching ? (
              <ActivityIndicator size="small" color={MARKETPLACE_COLORS.accentRed} />
            ) : (
              <Text 
                style={styles.loadMoreText}
                onPress={loadMoreMessages}
              >
                Load earlier messages
              </Text>
            )}
          </View>
        )}
        
        {groupedMessages.map((group, groupIndex) => (
          <View key={groupIndex} style={chatStyles.messageGroup}>
            <DateSeparator date={group.messages[0].timestamp} />
            <View style={chatStyles.messagesInGroup}>
              {group.messages.map((msg: ConversationMessage) => (
                msg.sender === 'user' ? (
                  <SentMessageBubble
                    key={msg.id}
                    text={msg.text}
                    timestamp={msg.timestamp}
                    files={msg.files}
                    replyTo={msg.replyTo}
                    onMenuPress={() => handleOpenActionsMenu(msg)}
                  />
                ) : (
                  <ReceivedMessageBubble
                    key={msg.id}
                    text={msg.text}
                    timestamp={msg.timestamp}
                    files={msg.files}
                    replyTo={msg.replyTo}
                    userName={conversationData?.otherUser?.name || 'User'}
                    userInitials={conversationData?.otherUser?.initials || 'U'}
                    onMenuPress={() => handleOpenActionsMenu(msg)}
                  />
                )
              ))}
            </View>
          </View>
        ))}
      </>
    );
  };

  // Loading state
  if (isLoading && !conversationData) {
    return (
      <View style={[styles.outerContainer, styles.loadingContainer]}>
        <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
      </View>
    );
  }

  // Error state
  if (isError && !conversationData) {
    return (
      <View style={[styles.outerContainer, styles.errorContainer]}>
        <Text style={styles.errorText}>Failed to load conversation</Text>
      </View>
    );
  }

  return (
    <View style={[
      styles.outerContainer,
      !isMobile && styles.outerContainerLarge,
    ]}>
      <KeyboardAvoidingView
        style={[
          styles.container, 
          { paddingTop: isMobile ? insets.top : 0 },
          !isMobile && maxChatWidth ? { 
            maxWidth: maxChatWidth, 
            alignSelf: 'center', 
            width: '100%',
            borderRadius: 16,
            overflow: 'hidden',
            marginTop: 24,
            marginBottom: 24,
          } : undefined,
        ]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        {/* Header - tap to view item details */}
        <ChatHeader
          title={conversationData?.listing?.title || 'Conversation'}
          subtitle={conversationData?.otherUser?.name || ''}
          imageUri={conversationData?.listing?.image}
          onClose={handleClose}
          onContentPress={handleHeaderPress}
        />

        {/* Chat Content */}
        <ScrollView
          ref={scrollViewRef}
          style={chatStyles.chatContent}
          contentContainerStyle={chatStyles.chatContentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={chatStyles.messagesContainer}>
            {renderMessages()}
          </View>
        </ScrollView>

        {/* Attachment Preview */}
        {attachments.length > 0 && (
          <AttachmentPreview 
            attachments={attachments} 
            onRemove={removeAttachment} 
          />
        )}

        {/* Reply Preview */}
        {replyingTo && (
          <ReplyPreview replyTo={replyingTo} onClear={clearReply} />
        )}

        {/* Message Input Bar */}
        <ChatInputBar
          value={messageText}
          onChangeText={setMessageText}
          onSend={handleSend}
          onAttach={handleAttach}
          inputRef={textInputRef}
          paddingBottom={isMobile ? Math.max(insets.bottom, 20) : 20}
          hasAttachments={attachments.length > 0}
        />

        {/* Message Actions Modal */}
        <MessageActionsModal
          visible={isActionsModalVisible}
          onClose={() => setIsActionsModalVisible(false)}
          onReply={handleReply}
          onReportConversation={handleOpenReportModal}
        />

        {/* Report Modal */}
        <ReportModal
          visible={isReportModalVisible}
          onClose={() => setIsReportModalVisible(false)}
          onSubmit={handleSubmitReport}
          hasAlreadyReported={hasReportedConversation}
          isSubmitting={isReporting}
        />
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  outerContainerLarge: {
    backgroundColor: MARKETPLACE_COLORS.background,
  },
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: MARKETPLACE_COLORS.textSecondary,
    fontSize: 16,
  },
  emptyMessages: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyMessagesText: {
    color: MARKETPLACE_COLORS.textSecondary,
    fontSize: 14,
  },
  loadMoreContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  loadMoreText: {
    color: MARKETPLACE_COLORS.accentRed,
    fontSize: 14,
    fontWeight: '500',
  },
});
